// src/pages/Dashboard/Balance/components/BalanceChart.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import styles from './BalanceChart.module.css';
import { balanceApi } from '../../../../api/balance/balanceApi';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const BalanceChart = () => {
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchBalanceStats();
  }, []);

  const fetchBalanceStats = async () => {
    try {
      setLoading(true);
      const data = await balanceApi.getBalanceStats();
      console.log('📊 Stats received:', data);
      setStats(data);
      setError(null);
    } catch (err) {
      setError('Failed to load balance statistics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.loadingMessage}>Loading balance statistics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.errorMessage}>{error}</div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const chartData = {
    labels: ['0-30 Days', '31-60 Days', '61-90 Days', '91+ Days'],
    datasets: [
      {
        label: 'Amount (₹)',
        data: [
          stats.breakdown.amount0to30,
          stats.breakdown.amount31to60,
          stats.breakdown.amount61to90,
          stats.breakdown.amount91Plus
        ],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(251, 146, 60, 0.8)',
          'rgba(234, 179, 8, 0.8)',
          'rgba(239, 68, 68, 0.8)'
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(251, 146, 60)',
          'rgb(234, 179, 8)',
          'rgb(239, 68, 68)'
        ],
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: function(context) {
            return '₹' + context.parsed.y.toLocaleString('en-IN');
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '₹' + (value / 1000000).toFixed(1) + 'M';
          },
          font: {
            size: 12
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        }
      },
      x: {
        ticks: {
          font: {
            size: 12
          }
        },
        grid: {
          display: false,
        }
      }
    }
  };

  const formatCurrency = (amount) => {
    return '₹' + (amount || 0).toLocaleString('en-IN');
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h2 className={styles.pageTitle}>Customer Balance Overview</h2>
      </div>

      <div className={styles.statsGrid}>
        <div className={`${styles.statCard} ${styles.statCardPrimary}`}>
          <div className={styles.statLabel}>Total Overdue</div>
          <div className={styles.statValue}>{formatCurrency(stats.totalOverdue)}</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>Total Overdue Customers</div>
          <div className={`${styles.statValue} ${styles.statValueDanger}`}>{stats.totalCustomers}</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>Customers 0-30 Days</div>
          <div className={styles.statValue}>{stats.breakdown.customers0to30}</div>
          <div className={styles.statSubtext}>{formatCurrency(stats.breakdown.amount0to30)}</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>Customers 31-60 Days</div>
          <div className={styles.statValue}>{stats.breakdown.customers31to60}</div>
          <div className={styles.statSubtext}>{formatCurrency(stats.breakdown.amount31to60)}</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>Customers 61-90 Days</div>
          <div className={styles.statValue}>{stats.breakdown.customers61to90}</div>
          <div className={styles.statSubtext}>{formatCurrency(stats.breakdown.amount61to90)}</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>Customers 91+ Days</div>
          <div className={styles.statValue}>{stats.breakdown.customers91Plus}</div>
          <div className={styles.statSubtext}>{formatCurrency(stats.breakdown.amount91Plus)}</div>
        </div>
      </div>

      <div className={styles.chartSection}>
        <div className={styles.chartHeader}>
          <h3 className={styles.sectionTitle}>Aging Analysis by Amount</h3>
        </div>
        <div className={styles.chartContainer}>
          <Bar ref={chartRef} data={chartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default BalanceChart;